import React, { useState, useEffect } from 'react';
import { X, Share2, UserPlus, Clock } from 'lucide-react';
import { apiService } from '../services/apiService';
import { useNotification } from '../context/NotificationContext';

interface ShareRunModalProps {
  runId: string;
  runName: string;
  onClose: () => void;
  onShared?: () => void;
}

interface ShareEntry {
  id: string;
  run_id: string;
  shared_with_user_id: string;
  shared_with_email?: string;
  shared_with_name?: string;
  expires_at: string | null;
  shared_at: string;
}

const ShareRunModal: React.FC<ShareRunModalProps> = ({ runId, runName, onClose, onShared }) => {
  const [userSearch, setUserSearch] = useState('');
  const [users, setUsers] = useState<{ id: string; email: string; display_name?: string }[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [shares, setShares] = useState<ShareEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const { showSuccess, showError } = useNotification();

  const loadShares = async () => {
    try {
      setLoading(true);
      const list = await apiService.getRunShares(runId);
      setShares(list);
    } catch (e) {
      showError('Error', 'Failed to load shares');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadShares();
  }, [runId]);

  useEffect(() => {
    if (!userSearch.trim()) {
      setUsers([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const list = await apiService.getUsers(userSearch);
        setUsers(list);
      } catch (_) {
        setUsers([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [userSearch]);

  const handleShare = async () => {
    if (!selectedUserId) {
      showError('Error', 'Select a user to share with');
      return;
    }
    setSharing(true);
    try {
      await apiService.shareRun(runId, selectedUserId, expiresAt.trim() || undefined);
      showSuccess('Success', 'Run shared successfully');
      setSelectedUserId('');
      setExpiresAt('');
      setUserSearch('');
      loadShares();
      onShared?.();
    } catch (e) {
      showError('Error', e instanceof Error ? e.message : 'Failed to share');
    } finally {
      setSharing(false);
    }
  };

  const handleRevoke = async (shareId: string) => {
    try {
      await apiService.revokeShare(runId, shareId);
      showSuccess('Success', 'Share revoked');
      loadShares();
    } catch (e) {
      showError('Error', 'Failed to revoke share');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Share2 size={20} />
            Share run: {runName}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Share with user
            </label>
            <input
              type="text"
              placeholder="Search by email or name..."
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            {users.length > 0 && (
              <ul className="mt-2 border border-gray-200 dark:border-gray-600 rounded-md divide-y divide-gray-200 dark:divide-gray-600 max-h-40 overflow-auto">
                {users.map(u => (
                  <li key={u.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedUserId(u.id);
                        setUserSearch(u.display_name || u.email);
                        setUsers([]);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${
                        selectedUserId === u.id ? 'bg-accent-50 dark:bg-accent-900/30' : ''
                      }`}
                    >
                      {u.display_name || u.email} {u.display_name && <span className="text-gray-500">({u.email})</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <Clock size={14} className="inline mr-1" />
              Expires at (optional)
            </label>
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={e => setExpiresAt(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <button
            onClick={handleShare}
            disabled={sharing || !selectedUserId}
            className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded-md flex items-center justify-center gap-2"
          >
            <UserPlus size={18} />
            {sharing ? 'Sharing...' : 'Share'}
          </button>

          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Shared with</h3>
            {loading ? (
              <p className="text-sm text-gray-500">Loading...</p>
            ) : shares.length === 0 ? (
              <p className="text-sm text-gray-500">Not shared with anyone yet.</p>
            ) : (
              <ul className="space-y-2">
                {shares.map(s => (
                  <li
                    key={s.id}
                    className="flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-700/50 rounded px-3 py-2"
                  >
                    <span>{s.shared_with_name || s.shared_with_email || s.shared_with_user_id}</span>
                    <div className="flex items-center gap-2">
                      {s.expires_at && (
                        <span className="text-xs text-gray-500">Expires {new Date(s.expires_at).toLocaleDateString()}</span>
                      )}
                      <button
                        onClick={() => handleRevoke(s.id)}
                        className="text-red-600 hover:underline text-xs"
                      >
                        Revoke
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareRunModal;
